import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { BaseChartDirective } from 'ng2-charts';
import {
  ChartConfiguration,
  ChartData,
  ChartOptions,
  Plugin,
  TooltipModel,
} from 'chart.js';
import { PieChartCardConfig } from '../card.component';
import { PrettyNumberPipeHelperService } from '../../../../services/pretty-number-pipe-helper/pretty-number-pipe-helper.service';
import {
  ChartTooltipContent,
  ChartTooltipManager,
  ChartTooltipService,
} from '../../../../services/chart-tooltip/chart-tooltip.service';
import { ChartThemeService } from '../../../../services/chart-theme/chart-theme.service';

@Component({
  selector: 'app-pie-chart-card-content',
  imports: [CommonModule, BaseChartDirective, MatIconModule],
  templateUrl: './pie-chart-card-content.component.html',
  styleUrl: './pie-chart-card-content.component.scss',
})
export class PieChartCardContentComponent implements OnChanges, OnDestroy {
  @Input() config!: PieChartCardConfig;

  readonly chartType: ChartConfiguration<'pie'>['type'] = 'pie';
  readonly chartPlugins: Plugin<'pie'>[] = [];

  chartData: ChartData<'pie', number[], string> = {
    labels: [],
    datasets: [],
  };
  chartOptions: ChartOptions<'pie'> = {};

  private readonly _tooltipManager: ChartTooltipManager<'pie'>;

  constructor(
    private readonly _prettyNumber: PrettyNumberPipeHelperService,
    tooltipService: ChartTooltipService,
    private readonly _theme: ChartThemeService,
  ) {
    this._tooltipManager = tooltipService.createManager<'pie'>({
      transform: 'translate(-50%, calc(-100% - 12px))',
      render: ({ tooltip }) => this._renderTooltip(tooltip),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this._rebuildChart();
    }
  }

  ngOnDestroy(): void {
    this._tooltipManager.destroy();
  }

  get horizonYearsLabel(): string {
    const years = this._resolveHorizonYears();
    return years ? years.toString() : '?';
  }

  private _rebuildChart(): void {
    this._tooltipManager.hide();

    if (!this.config || !this.config.slices?.length) {
      const unit = this.config?.unit;
      this.chartData = { labels: [], datasets: [] };
      this.chartOptions = this._buildChartOptions(0, unit);
      return;
    }

    const colors = this._theme.getSliceColors(
      this.config.colorIndex ?? 0,
      this.config.slices.length,
    );
    const data = this.config.slices.map((slice) => this._sanitize(slice.value));
    const labels = this.config.slices.map((slice) => slice.label);
    const total = this._sanitize(this.config.total);
    const unit = this.config.unit;

    this.chartData = {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderWidth: 1,
          hoverOffset: 6,
        },
      ],
    };

    this.chartOptions = this._buildChartOptions(total, unit);
  }

  private _buildChartOptions(
    total: number,
    unit: string | undefined,
  ): ChartOptions<'pie'> {
    return {
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          align: 'start',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            font: {
              family: 'Poppins',
              size: 14,
            },
          },
        },
        tooltip: {
          enabled: false,
          external: (context) => this._tooltipManager.handler(context as any),
        },
      },
    };
  }

  private _resolveHorizonYears(): number | undefined {
    if (!this.config) {
      return undefined;
    }

    const direct = this.config.timeHorizonYears;
    if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) {
      return direct;
    }

    const match = this.config.yearLabel?.match(/\d+/);
    if (match?.[0]) {
      const numeric = Number(match[0]);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }

    return undefined;
  }

  private _renderTooltip(
    tooltip: TooltipModel<'pie'>,
  ): ChartTooltipContent | null {
    if (!tooltip || !tooltip.dataPoints?.length || !this.config) {
      return null;
    }

    const dataPoint = tooltip.dataPoints[0];
    const slice = this.config.slices?.[dataPoint.dataIndex];
    if (!slice) {
      return null;
    }

    const total = this._sanitize(this.config.total);
    const rawValue = this._sanitize(
      (dataPoint.parsed as number | undefined) ?? slice.value,
    );
    const formattedValue = this._prettyNumber.transform(
      slice.rawValue ?? rawValue,
      false,
      this.config.unit,
    );
    const percentLabel = this._formatPercent(rawValue, total);

    const titleLabel = tooltip.title?.[0] ?? slice.label ?? '';
    const title = this.config.yearLabel
      ? `${this.config.yearLabel}`
      : titleLabel;

    const indicatorColor =
      tooltip.labelColors?.[0]?.backgroundColor?.toString() ?? undefined;

    return {
      title,
      lines: [
        {
          label: slice.label ?? titleLabel,
          value: formattedValue,
          extra: percentLabel || undefined,
          indicatorColor,
        },
      ],
    };
  }

  private _sanitize(value: number | undefined | null): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  private _formatPercent(value: number, total: number): string {
    if (!total) {
      return '';
    }
    const percent = (value / total) * 100;
    if (percent <= 0) {
      return '';
    }
    const hasDecimal = Math.abs(percent % 1) > 0.0001;
    const rounded = hasDecimal ? percent.toFixed(1) : percent.toFixed(0);
    return `${rounded}%`;
  }
}
